import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ProductionGrainsActions } from "@/components/common/section-components/action/ProductionGrainsActions";
import { ProductionGrainsFilter } from "@/components/common/section-components/filters/ProductionGrainsFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { isEmpty } from "@/utils/query-string";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { GetProductionGrainsListProps } from "@/services/production-grains.service";
import {
  clearFiltersHandler,
  fetchProductionGrainsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { productionGrainsHeaderElements } from "./schema/header-elements";
import {
  ProductionGrainListEntity,
  ProductionGrainsListResponse,
  productionGrainsFilterSchema,
} from "./schema/production-grains.schema";

export const ProductionGrainsPage = () => {
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState(false);

  const [loadingProdGrains, setLoadingProdGrains] = useState(false);
  const [productionGrains, setProductionGrains] =
    useState<ProductionGrainsListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetProductionGrainsListProps> | undefined
  >();

  const { fetchPermissions, hasWriteAccess } = useRoles();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(productionGrainsFilterSchema),
    defaultValues: {
      Nombre: "",
      AplicaPPMB: undefined,
      SumaHA: undefined,
      GranosAdmis: "",
    },
  });

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate =
    hasSelection &&
    selection.length < (productionGrains?.listado?.length ?? 0);

  const fetchProductionGrains = async (
    filters?: Partial<GetProductionGrainsListProps>,
    showLoad = true,
  ) => {
    await fetchProductionGrainsHandler({
      setLoadingProductionGrains: setLoadingProdGrains,
      setProductionGrains,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      setPage,
      fetchProductionGrains,
    });
    setSelection([]);
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      getValues,
      setOpenFilters,
      submitFilters: async () =>
        submitFiltersHandler({
          event: getValues(),
          setOpenFilters,
          setFiltersApplied,
          setCurrentFilters,
          setPage,
          fetchProductionGrains,
        }),
    });
    setSelection([]);
  };

  useEffect(() => {
    if (isActive) {
      fetchProductionGrains();
    }
  }, [isActive]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    setSelection([]);
    fetchProductionGrains({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.PRODUCTION_GRAINS_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: ProductionGrainListEntity) => (
    <ProductionGrainsActions handleDetail={handleDetail} item={item} />
  );

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const items =
    productionGrains?.listado?.map((el: ProductionGrainListEntity) => ({
      ...el,
      aplicaPPMB: el.aplicaPPMB ? "Sí" : "No",
      sumaHA: el.sumaHA ? "Sí" : "No",
      granoAdmis: el.granoAdmisDesc,
    })) ?? [];

  const handleAddProdGrain = () => {
    navigate(AppRoutes.PRODUCTION_GRAINS_CREATION);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="production-grains-page">
      <SectionHeader
        title={"Maestro de Granos Productivos"}
        addButtonLabel="Agregar"
        handleAdd={handleAddProdGrain}
        loading={loadingProdGrains}
        showButtons={hasWriteAccess(PermissionCode.PRODUCTION_GRAINS)}
      />

      <ProductionGrainsFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingProdGrains}
        loadingFilters={loadingProdGrains}
        filtersApplied={filtersApplied}
        disableClear={loadingProdGrains}
        disableApply={loadingProdGrains || filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingProdGrains && !productionGrains?.listado?.length) && (
          <NoData />
        )}

        {Boolean(!loadingProdGrains && productionGrains?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              items={items}
              headers={productionGrainsHeaderElements}
              actionElements={getActionElements}
              noActions={!hasWriteAccess(PermissionCode.PRODUCTION_GRAINS)}
              noSelection
            />

            {Boolean(productionGrains?.totalPages && productionGrains.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={productionGrains?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
